import * as React from "react"
import { SpaceProps } from "styled-system"
import { INNER_MAX_WIDTH } from "../../constants"
import { Flex } from "../Flex"
import { Icon } from "../Icon"
import { Text, TextProps } from "../Text"
import { IconTypes } from "../Icon/IconMap"
import {
	SubmenuTriggerStyled,
	SubmenuMenuStyled,
	DividerStyled,
} from "./styled"
import { useClickOutside, useClickNavElement } from "../../hooks"
import { ChildrenProps } from "../../@types/@core"
import { PrimaryMenuChevronStyled } from ".."

export interface SubMenuItemProps
	extends TextProps,
		React.HTMLAttributes<HTMLLIElement> {
	icon?: IconTypes
	active?: boolean
}

export interface SubmenuComponent {
	Menu: React.FC<ChildrenProps>
	List: React.FC<SpaceProps & React.HTMLAttributes<HTMLUListElement>>
	Item: React.FC<SubMenuItemProps>
	Trigger: React.FC<ChildrenProps>
	Divider: React.FC<ChildrenProps>
}

export interface SubMenuProps {
	zIndex?: number
	children?: React.ReactNode
}

const getSubmenuLayoutOffset = function () {
	const paddingX = 6
	let submenuOffset = 0

	if (typeof document !== undefined) {
		if (window.innerWidth / 8 > INNER_MAX_WIDTH + paddingX) {
			submenuOffset = (-window.innerWidth / 2 + (INNER_MAX_WIDTH * 8) / 2) / 8
		} else {
			submenuOffset = -paddingX / 2
		}
	}

	return submenuOffset
}

export interface SubmenuContextValue {
	open: boolean
	toggle: () => void
	closeMenu: () => void
}

export const SubmenuContext = React.createContext({
	open: false,
	toggle: () => {},
	closeMenu: () => {},
})

const Submenu: React.FC<SubMenuProps> & SubmenuComponent = (props) => {
	const [open, setOpen] = React.useState(false)
	const toggle = () => setOpen(!open)

	const submenuLayoutOffset = getSubmenuLayoutOffset()
	const posSubmenuLayoutOffset = Math.abs(submenuLayoutOffset)

	const closeMenu = () => setOpen(false)

	return (
		<SubmenuContext.Provider value={{ open, toggle, closeMenu }}>
			<Flex
				position="relative"
				ml="auto"
				backgroundColor={open ? "blue33" : "blue11"}
				justifyContent="center"
				mr={submenuLayoutOffset}
				zIndex={props.zIndex}
			>
				<Flex
					w={1}
					pr={{ _: 2.5, l: posSubmenuLayoutOffset }}
					borderLeft
					pl={{ _: 2, l: 5 }}
					my={{ _: 1, l: 2 }}
				>
					{props.children}
				</Flex>
			</Flex>
		</SubmenuContext.Provider>
	)
}

Submenu.Trigger = (props) => {
	const triggerRef = React.createRef<HTMLButtonElement>()
	const { toggle, closeMenu, open } = React.useContext(SubmenuContext)

	useClickOutside(triggerRef, closeMenu)
	useClickNavElement(closeMenu)

	return (
		<SubmenuTriggerStyled
			ref={triggerRef}
			onClick={toggle}
			aria-expanded={open ? "true" : "false"}
		>
			{props.children}
			<PrimaryMenuChevronStyled icon="chevron-down" size={2} />
		</SubmenuTriggerStyled>
	)
}

Submenu.Menu = (props) => {
	const { open } = React.useContext(SubmenuContext)

	return (
		<SubmenuMenuStyled aria-hidden={open ? "false" : "true"}>
			{props.children}
		</SubmenuMenuStyled>
	)
}

Submenu.List = (props) => (
	<Flex as="ul" flexDirection="column" {...props}>
		{props.children}
	</Flex>
)

Submenu.Item = (props) => (
	<Text
		display="flex"
		as="li"
		fontWeight={props.active ? "bold" : "normal"}
		{...props}
	>
		{props.icon && <Icon icon={props.icon} size={3} mr={1} />}
		{props.children}
	</Text>
)

Submenu.Divider = (props) => <DividerStyled />

export { Submenu }
